import type { Subscription } from './types';
import { SubscriptionIcon } from '@/shared/ui/SubscriptionIcon';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { MonoAmount } from '@/shared/ui/MonoAmount';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { cn } from '@/shared/lib/cn';
import { useT } from '@/shared/lib/i18n';

interface Props {
  sub: Subscription;
  amountDue: number;
  status?: 'paid' | 'due' | 'overdue' | 'pending' | 'draft';
  onOpen?: () => void;
  onPay?: () => void;
}

const CURRENCY_SYMBOL: Record<string, string> = { RUB: '₽', USD: '$', EUR: '€' };

function periodText(period: { interval: number; unit: string }, t: ReturnType<typeof useT>): string {
  const u = period.unit === 'months'
    ? t('common.periodMonthsShort')
    : period.unit === 'weeks'
      ? t('common.periodWeeksShort')
      : t('common.periodDaysShort');
  return period.interval === 1 ? `1 ${u}` : `${period.interval} ${u}`;
}

function statusCopy(status: NonNullable<Props['status']>, t: ReturnType<typeof useT>) {
  switch (status) {
    case 'paid':
      return { badge: t('subscription.memberCard.paid'), variant: 'active' as const, amount: 'paid' as const };
    case 'pending':
      return { badge: t('subscription.memberCard.pending'), variant: 'confirm' as const, amount: 'muted' as const };
    case 'overdue':
      return { badge: t('subscription.memberCard.overdue'), variant: 'overdue' as const, amount: 'debt' as const };
    case 'draft':
      return { badge: t('subscription.memberCard.draft'), variant: 'draft' as const, amount: 'muted' as const };
    default:
      return { badge: t('subscription.memberCard.due'), variant: 'due' as const, amount: 'debt' as const };
  }
}

function SubIcon({ sub }: { sub: Subscription }) {
  const letter = sub.name.trim()[0]?.toUpperCase() ?? '?';
  const isEmoji = sub.icon.length > 0 && !/^[a-z0-9][a-z0-9-]*$/.test(sub.icon);
  const isSlug = sub.icon.length > 0 && /^[a-z0-9][a-z0-9-]*$/.test(sub.icon);

  if (isEmoji) {
    return (
      <span className="w-[34px] h-[34px] rounded-[9px] bg-brand-gradient flex items-center justify-center text-[19px] shadow-[0_3px_8px_rgba(18,120,90,0.28)] leading-none">
        {sub.icon}
      </span>
    );
  }
  if (isSlug) {
    return (
      <img
        src={`/api/v1/icons/${sub.icon}/default`}
        alt={sub.name}
        className="w-[34px] h-[34px] rounded-[9px] object-contain bg-white/55 border border-white/55 p-1"
      />
    );
  }
  return <SubscriptionIcon letter={letter} size="md" />;
}

export function SubscriptionCardMember({
  sub,
  amountDue,
  status = amountDue > 0 ? 'due' : 'paid',
  onOpen,
  onPay,
}: Props) {
  const t = useT();
  const sym = CURRENCY_SYMBOL[sub.currency] ?? sub.currency;
  const copy = statusCopy(status, t);
  const amountText = `${Math.max(0, amountDue).toLocaleString('ru')} ${sym}`;
  const isPaid = status === 'paid';
  const isPending = status === 'pending';
  const isDraft = status === 'draft';

  return (
    <div
      className={cn(
        'w-full rounded-[20px] p-[15px] transition-all',
        'glass-content',
        onOpen && 'cursor-pointer active:scale-[0.985]',
      )}
      onClick={onOpen}
      role={onOpen ? 'button' : undefined}
      aria-label={onOpen ? t('common.open', { name: sub.name }) : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onKeyDown={(event) => {
        if (!onOpen) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-[9px] min-w-0">
          <SubIcon sub={sub} />
          <div className="min-w-0">
            <p className="text-[16px] font-bold truncate">{sub.name}</p>
            <p className="text-[11px] text-text-secondary font-mono font-tabular mt-0.5">
              {periodText(sub.period, t)}
            </p>
          </div>
        </div>
        <StatusBadge variant={copy.variant} label={copy.badge} className="flex-shrink-0" />
      </div>

      <div className="flex items-end justify-between gap-3 mt-[13px]">
        <div className="min-w-0">
          {isDraft ? (
            <MonoAmount value={t('common.dash')} variant="muted" size="md" />
          ) : isPaid ? (
            <MonoAmount value={t('subscription.memberCard.paidAmount', { currency: sym })} variant="paid" size="md" />
          ) : (
            <MonoAmount value={amountText} variant={copy.amount} size="lg" />
          )}
          <div
            className={cn(
              'flex items-center gap-[5px] mt-[6px] text-[11px] font-mono font-tabular opacity-80',
              isPaid ? 'text-status-paid' : isPending || isDraft ? 'text-status-warn' : 'text-status-debt',
            )}
          >
            <span
              className={cn(
                'w-[6px] h-[6px] rounded-full inline-block flex-shrink-0',
                isPaid ? 'bg-status-paid' : isPending || isDraft ? 'bg-status-warn' : 'bg-status-debt',
              )}
            />
            {isDraft
              ? t('subscription.memberCard.draftHint')
              : isPaid
                ? t('subscription.memberCard.allClear')
                : isPending
                  ? t('subscription.memberCard.waitingHolder')
                  : t('subscription.memberCard.currentDebt')}
          </div>
        </div>

        {!isPaid && !isPending && !isDraft && onPay && (
          <div onClick={(event) => event.stopPropagation()}>
            <PrimaryButton
              fullWidth={false}
              className="min-w-[104px] px-[18px] py-[9px] text-[14px] rounded-[12px] shadow-[0_8px_18px_rgba(18,120,90,0.35)]"
              onClick={() => {
                onPay();
              }}
            >
              {t('subscription.memberCard.iPaid')}
            </PrimaryButton>
          </div>
        )}
      </div>
    </div>
  );
}
